import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import logger from 'utils/logger';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useApolloClient } from '@apollo/client';
import { apiNetworkRequest } from 'actions/helpers';
import { authenticate } from 'actions/auth';
import { PlayerContext } from 'components/Player';
import * as routerActions from 'router/actions';
import { RouterLocation } from 'router/location';
import { Spinner } from 'components';
import { useProfilesQuery } from './GetProfiles.generated';
import { OverlayContainer, SpinnerContainer } from './Styles';
import {
  MAX_PROFILES,
  PROFILE_MESSAGE_TYPES,
  PROFILE_ROUTE_NAMES,
  persistSelectedProfileId,
  recordProfileActivityTimestamp,
  clearProfileActivityTimestamp,
  isRouteAllowedForProfilePrompt,
  getStoredProfileId,
  isInactiveForProfileReselect,
  cloneRouterLocation,
  ProfileType,
  getProfileBroadcastChannelName,
  PROFILE_ACTIVITY_INTERVAL,
} from './utils';

interface ProfileContextType {
  isSelecting: boolean;
  selectProfile: (profileId: string) => Promise<void>;
  profilesLoading: boolean;
  profilesError?: any;
  profiles?: Array<any>;
  currentProfile?: any;
  refreshProfiles: () => void;
  isFeatureEnabled: boolean;
  hasReachedProfileLimit: boolean;
  returnLocation?: RouterLocation;
  setReturnLocation: (location?: RouterLocation) => void;
  editProfile?: ProfileType;
  setEditProfile: React.Dispatch<React.SetStateAction<ProfileType | undefined>>;
  openReturnLocation: (options?: { reload?: boolean }) => void;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

// Top-level provider that loads profiles, handles cross-tab sync and navigation,
// and exposes the current profile information to the rest of the application.
export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const [isSelecting, setIsSelecting] = useState(false);
  const [returnLocation, setReturnLocation] = useState<RouterLocation | undefined>();
  const [editProfile, setEditProfile] = useState<ProfileType | undefined>();
  const dispatch = useDispatch();
  const apolloClient = useApolloClient();
  const playerContext = useContext(PlayerContext);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const instanceIdRef = useRef<string>('');
  if (!instanceIdRef.current) {
    instanceIdRef.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  const {
    isLoggedIn,
    isAnonymousUser,
    multipleProfilesEnabled,
    location,
    partner,
  } = useSelector((state: any) => ({
    isLoggedIn: state.auth.isLoggedIn,
    isAnonymousUser: state.auth.isAnonymousUser,
    multipleProfilesEnabled: !!state.settings?.features?.multipleProfiles,
    location: state.router.location,
    partner: state.settings?.partner,
  }), shallowEqual);

  const isFeatureEnabled = !!multipleProfilesEnabled;
  const shouldLoadProfiles = isFeatureEnabled && isLoggedIn && !isAnonymousUser;

  // Load profiles ASAP so they are available across the app via context
  const {
    data: profilesData,
    loading: profilesLoading,
    error: profilesError,
    refetch: refetchProfiles,
  } = useProfilesQuery({
    skip: !shouldLoadProfiles,
    errorPolicy: 'ignore',
  });

  const profiles = useMemo(
    () => shouldLoadProfiles ? profilesData?.viewer?.profiles : undefined,
    [shouldLoadProfiles, profilesData],
  );
  const currentProfile = useMemo(
    () => shouldLoadProfiles ? profiles?.find((p: any) => p?.current) : undefined,
    [shouldLoadProfiles, profiles],
  );
  const profileCount = profiles?.length ?? 0;
  const hasReachedProfileLimit = profileCount >= MAX_PROFILES;

  // Track tab close / navigation away and periodically record profile activity timestamp.
  // This is used later to decide whether to force the user to re-select a profile.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const recordActivityIfNeeded = () => {
      const storedProfileId = getStoredProfileId();

      if (profiles && profiles.length <= 1) {
        clearProfileActivityTimestamp();
        return;
      }

      if (!storedProfileId) {
        return;
      }

      recordProfileActivityTimestamp();
    };

    window.addEventListener('pagehide', recordActivityIfNeeded);
    window.addEventListener('beforeunload', recordActivityIfNeeded);
    document.addEventListener('visibilitychange', recordActivityIfNeeded);

    const intervalId = setInterval(recordActivityIfNeeded, PROFILE_ACTIVITY_INTERVAL);

    recordActivityIfNeeded();

    return () => {
      window.removeEventListener('pagehide', recordActivityIfNeeded);
      window.removeEventListener('beforeunload', recordActivityIfNeeded);
      document.removeEventListener('visibilitychange', recordActivityIfNeeded);
      clearInterval(intervalId);
    };
  }, [profiles]);

  // Set up a BroadcastChannel to synchronize selected profile and activity
  // across multiple browser tabs/windows.
  useEffect(() => {
    if (typeof window === 'undefined' || !(window as any).BroadcastChannel || !isFeatureEnabled) {
      return;
    }

    if (!shouldLoadProfiles) {
      return;
    }

    const channelName = getProfileBroadcastChannelName(partner);
    const channel = new window.BroadcastChannel(channelName);
    broadcastChannelRef.current = channel;

    channel.onmessage = (event: MessageEvent) => {
      const data = event?.data || {};
      if (data.sourceId && data.sourceId === instanceIdRef.current) {
        return;
      }
      if (data.type === PROFILE_MESSAGE_TYPES.profileSelected && data.payload?.profileId) {
        persistSelectedProfileId(data.payload.profileId);
        if (data.payload?.shouldReload) {
          window.location.reload();
        }
      } else if (data.type === PROFILE_MESSAGE_TYPES.requestProfile) {
        const storedProfileId = getStoredProfileId();
        if (storedProfileId) {
          channel.postMessage({
            type: PROFILE_MESSAGE_TYPES.profileSelected,
            payload: { profileId: storedProfileId },
            sourceId: instanceIdRef.current,
          });
        }
      }
    };

    const storedProfileId = getStoredProfileId();
    if (!storedProfileId) {
      channel.postMessage({
        type: PROFILE_MESSAGE_TYPES.requestProfile,
        sourceId: instanceIdRef.current,
      });
    }

    return () => {
      channel.close();
      broadcastChannelRef.current = null;
    };
  }, [shouldLoadProfiles]);

  // After profiles are loaded, decide whether to show the profile selection screen
  // based on stored profile id, inactivity time and current route.
  useEffect(() => {
    if (!shouldLoadProfiles || !profileCount) {
      return;
    }
    // wait to receive messages from other tabs. see prev useEffect
    const timeoutId = window.setTimeout(() => {
      const storedProfileId = getStoredProfileId();
      const isInactiveLongTime = isInactiveForProfileReselect();

      if (
        ((!storedProfileId && isInactiveLongTime && profileCount > 1) || !currentProfile)
        && isRouteAllowedForProfilePrompt(location?.name)
      ) {
        setReturnLocation(cloneRouterLocation(location));
        dispatch(routerActions.replace({ name: 'profiles' }));
      } else if (!isInactiveLongTime) {
        if (!storedProfileId && currentProfile?.id) {
          persistSelectedProfileId(currentProfile.id);
        }
      }
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [shouldLoadProfiles, !profileCount]);

  // Redirect to profiles route when URL changes and there's no current profile
  useEffect(() => {
    if (!shouldLoadProfiles || !profileCount || !location) {
      return;
    }
    // Don't redirect if already on a profile route
    if (PROFILE_ROUTE_NAMES.includes(location.name)) {
      return;
    }
    // Redirect if there's no current profile
    if (!currentProfile) {
      setReturnLocation(cloneRouterLocation(location));
      dispatch(routerActions.replace({ name: 'profiles' }));
    }
  }, [location, !currentProfile]);

  // Navigate back to the stored return location (or home) after profile selection,
  // optionally triggering a full reload.
  const openReturnLocation = useCallback((options?: { reload?: boolean }) => {
    let targetLocation: RouterLocation = returnLocation ?? { name: 'home' };

    if (typeof location?.query?.redirectTo === 'string') {
      try {
        const parsedLocation = JSON.parse(location.query.redirectTo) as RouterLocation;
        if (!PROFILE_ROUTE_NAMES.includes(parsedLocation.name)) {
          targetLocation = parsedLocation;
          setReturnLocation(undefined);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          logger.warn('Failed to parse redirectTo query param', error);
        }
      }
    }

    if (options?.reload) {
      dispatch(routerActions.pushWithReload(targetLocation));
    } else {
      dispatch(routerActions.push(targetLocation));
    }
    setReturnLocation(undefined);
  }, [returnLocation, dispatch, location]);

  // Trigger profile selection on the backend, update auth token and
  // propagate the selection across tabs.
  const selectProfile = useCallback(async (profileId: string) => {
    if (!isFeatureEnabled) {
      return;
    }

    // If the requested profile is already current, avoid a redundant backend call
    if (currentProfile?.id === profileId) {
      persistSelectedProfileId(profileId);
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({
          type: PROFILE_MESSAGE_TYPES.profileSelected,
          payload: { profileId, shouldReload: true },
          sourceId: instanceIdRef.current,
        });
      }
      return;
    }

    setIsSelecting(true);
    try {
      const result = await dispatch<any>(apiNetworkRequest('auth.selectProfile', { profileId }));
      if (result?.userId && result?.token) {
        persistSelectedProfileId(profileId);
        dispatch(authenticate(result.userId, result.token));
        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({
            type: PROFILE_MESSAGE_TYPES.profileSelected,
            payload: { profileId, shouldReload: true },
            sourceId: instanceIdRef.current,
          });
        }
      }
    } catch (e) {
      logger.error('selectProfile error', e);
    }
  }, [dispatch, apolloClient, playerContext, refetchProfiles, isFeatureEnabled, shouldLoadProfiles, currentProfile]);

  const refreshProfiles = useCallback(() => {
    if (shouldLoadProfiles) {
      void refetchProfiles?.();
    }
  }, [shouldLoadProfiles, refetchProfiles]);

  const contextValue = useMemo(() => ({
    isSelecting,
    selectProfile,
    profilesLoading: shouldLoadProfiles ? profilesLoading : false,
    profilesError: shouldLoadProfiles ? profilesError : undefined,
    profiles,
    currentProfile,
    refreshProfiles,
    isFeatureEnabled,
    hasReachedProfileLimit,
    returnLocation,
    setReturnLocation,
    editProfile,
    setEditProfile,
    openReturnLocation,
  }), [
    isSelecting,
    selectProfile,
    shouldLoadProfiles,
    profilesLoading,
    profilesError,
    profiles,
    currentProfile,
    refreshProfiles,
    isFeatureEnabled,
    hasReachedProfileLimit,
    returnLocation,
    setReturnLocation,
    editProfile,
    setEditProfile,
    openReturnLocation,
  ]);

  return (
    <ProfileContext.Provider value={contextValue}>
      {isSelecting && (
        <OverlayContainer>
          <SpinnerContainer>
            <Spinner size={4} />
          </SpinnerContainer>
        </OverlayContainer>
      )}
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfileContext() {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfileContext must be used within a ProfileProvider');
  }
  return context;
}

